import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'wasp/client/operations';
import { getNoticeById, deleteNotice } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  ArrowLeft,
  Bell, 
  AlertTriangle, 
  Info, 
  Calendar,
  User,
  MapPin,
  Edit3,
  Trash2,
  Eye,
  Pin,
  Clock,
  Tag,
  Share2,
  MessageSquare
} from 'lucide-react';
import { 
  IndividualViewPageLayout, 
  SidebarSection, 
  ActionButtons, 
  DetailField,
  ActionButtonLink,
  ActionButtonDanger
} from '../../components/layout';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { isAdministrator } from '../../client/lib/platformRole';
import { getPriorityColorClass } from './noticeConstants';

export default function ViewNoticePage() {
  const { noticeId, slug } = useParams<{ noticeId: string; slug?: string }>();
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, slug, selectedLocation);
  const noticesListPath = locationSectionPath(locSlug, 'notices');
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch notice data
  const { 
    data: notice, 
    isLoading, 
    error 
  } = useQuery(getNoticeById, { noticeId: noticeId! });

  const getPriorityColor = (priority: string) => getPriorityColorClass(priority);

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this notice? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteNotice({ noticeId: noticeId! });
      navigate(noticesListPath);
    } catch (error) {
      console.error('Error deleting notice:', error);
      alert('Failed to delete notice. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'announcement': return <Bell className="w-5 h-5" />;
      case 'warning': return <AlertTriangle className="w-5 h-5" />;
      case 'info': return <Info className="w-5 h-5" />;
      case 'event': return <Calendar className="w-5 h-5" />;
      default: return <Bell className="w-5 h-5" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading notice...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !notice) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Notice Not Found</h3>
            <p className="text-gray-600 mb-4">The notice you're looking for doesn't exist or has been removed.</p>
            <button
              onClick={() => navigate(noticesListPath)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Notice Board
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isOwner = !!user && (notice.authorId === user.id || isAdministrator(user));
  const currentPageUrl = typeof window !== 'undefined' ? window.location.href : '';

  const breadcrumbItems = [
    {
      label: 'About',
      dropdownItems: [
        { label: 'Notices', href: noticesListPath },
        { label: 'Events/Activities', href: locationSectionPath(locSlug, 'events') },
        { label: 'Tenants', href: locationSectionPath(locSlug, 'tenants') },
        { label: 'Facilities', href: locationSectionPath(locSlug, 'facilities') },
      ]
    },
    {
      label: 'Notices',
      isClickable: true,
      href: noticesListPath
    }
  ];

  const sidebarContent = (
    <>
      {isOwner && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButtonLink
              to={locationSectionPath(locSlug, 'notices', notice.id, 'edit')}
              icon={Edit3}
            >
              Edit Notice
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
              disabled={isDeleting}
              icon={Trash2}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Deleting...
                </>
              ) : (
                'Delete Notice'
              )}
            </ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="By" value={notice.author?.username || notice.author?.email || 'Anonymous'} />
          <DetailField label="Posted" value={formatDate(notice.createdAt)} />
          {notice.updatedAt !== notice.createdAt && (
            <DetailField label="Updated" value={formatDate(notice.updatedAt)} />
          )}
          <DetailField label="Location" value={notice.location?.name || 'N/A'} />
          <DetailField label="Views" value={notice.viewCount || 0} />
          {notice.comments && notice.comments.length > 0 && (
            <DetailField label="Comments" value={notice.comments.length} />
          )}
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
              {/* Notice Header */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{notice.title}</h1>
                
                {notice.description && (
                  <p className="text-xl text-gray-600 mb-6">{notice.description}</p>
                )}
              </div>

              {/* Notice Content */}
              <div className="prose prose-lg max-w-none mb-8">
                {notice.contentBody && (
                  <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {notice.contentBody}
                  </div>
                )}
              </div>

              {/* Images */}
              {notice.images && notice.images.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Images</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {notice.images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Notice image ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => {
                          // TODO: Implement image lightbox
                          window.open(image, '_blank');
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Status Information */}
              {(!notice.isApproved || notice.isArchived) && (
                <div className="mb-8 p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Notice Status</span>
                  </div>
                  <div className="mt-2 text-sm text-yellow-700">
                    {!notice.isApproved && <p>• This notice is pending approval</p>}
                    {notice.isArchived && <p>• This notice has been archived</p>}
                  </div>
                </div>
              )}
    </IndividualViewPageLayout>
  );
}


